import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory, useParams, useLocation } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";
import NavPills from "components/NavPills/NavPills";
import Button from "components/CustomButtons/Button";

import {
  RevenueContent,
  ExpenseContent
} from "../../common/ChapterFinancialReport";

import { actionCreators as actionCreatorsAdmin } from "../../../_actions/adminActions";
import Loading from "../../common/Loading";
import {
  CHAPTER_FINANCIAL_STATUS,
  CHAPTER_STATUS,
  REPORT_ITEM_TYPE
} from "../../../constants";
import { formatDate } from "../../../utils";
import CashContent from "../../common/ChapterFinancialReport/CashContent/CashContent";
import SummaryContent from "../../common/ChapterFinancialReport/SummaryContent";
import WelcomeContent from "../../common/ChapterFinancialReport/WelcomeContent";
import { Box } from "@material-ui/core";
import HelpDialog from "../../common/HelpDialog";
import { ArrowBack } from "@material-ui/icons";
import AcceptReportDialog from "./AcceptReportDialog";

const useStyles = makeStyles(theme => ({
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  marginTop: {
    marginTop: theme.spacing(3)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  tabContentItem: {
    margin: theme.spacing(1)
  },
  tabSubTitle: {
    background: "#aaa",
    padding: 8,
    marginBottom: 16,
    textTransform: "capitalize"
  },
  tabItem: {
    padding: "4px 8px"
  },
  address: {
    whiteSpace: "pre-line",
    fontSize: "1rem",
    fontWeight: 400
  },
  borderBottom: {
    borderBottom: "1px solid #090909",
    width: 150,
    textAlign: "right"
  },
  marginLeft: {
    marginLeft: 16
  },
  scholarship: {
    whiteSpace: "pre-line",
    marginRight: 10,
    flex: 1
  },
  scholarshipAmount: {
    textAlign: "right",
    width: 90
  },
  cursorPointer: {
    cursor: "pointer"
  },
  addItem: {
    cursor: "pointer",
    marginLeft: 10,
    fontWeight: "bold"
  },
  dividerMargin: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  actionWrapper: {
    position: "absolute",
    top: theme.spacing(1),
    right: 0
  }
}));

const TabContent = props => {
  const contentRef = useRef(null);
  const { active, setActive, isEnd } = props;

  const scroll = useCallback(() => {
    contentRef.current.scrollIntoView();
  }, [contentRef]);

  const onBack = useCallback(() => {
    setActive(active - 1);
    scroll();
  }, [active]);

  const onContinue = useCallback(() => {
    setActive(active + 1);
    scroll();
  }, [active]);
  return (
    <>
      <div ref={contentRef}>{props.children}</div>
      <Box display="flex" justifyContent="flex-end" mt={3}>
        {active > 0 && (
          <Button color="rose" onClick={onBack}>
            Back
          </Button>
        )}
        {active > 0 && !isEnd && <Box mr={2} />}
        {!isEnd && (
          <Button color="rose" onClick={onContinue}>
            Continue
          </Button>
        )}
      </Box>
    </>
  );
};

const FinancialReportForm = props => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState();
  const [isHelpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpData, setHelpData] = useState({ type: -1, subType: -1 });
  const isCreate = id === "add";
  const [isEditable, setIsEditable] = useState(true);

  const {
    getChapterFinancial,
    getChapter,
    activeChapter,
    activeChapterFinancial,
    getChapterFinancialsDefaults,
    getChapterSummaryReport,
    submitChapterFinancial,
    chapterFinancialsDefaults,
    createReportItem,
    updateReportItem,
    removeReportItem,
    updateReportItems,
    updateReportItemsPut,
    initialReportItems,
    updateReportW2,
    updateVotingCount,
    createFinancialAccount,
    updateFinancialAccount,
    removeFinancialAccount,
    getChapterFinancialByYear,
    getChapterFinancialById,
    acceptChapterFinancial,
    rejectChapterFinancial,
    isFetching,
    resetIsFetched,
    isFetched
  } = props;

  const [activeTab, setActiveTab] = useState(0);
  const [prevChapterFinancial, setPrevChapterFinancial] = useState({});

  useEffect(() => {
    if (!isFetching.CHAPTER_FINANCIAL && !isFetched.CHAPTER_FINANCIAL) {
      if (!isCreate) {
        getChapterFinancial(id);
      }
      getChapterFinancialsDefaults();
    }
  }, [id]);

  useEffect(() => {
    if (query && query.get("chapter")) {
      getChapter(query.get("chapter"));
    }
  }, []);

  useEffect(() => {
    if (activeChapterFinancial) {
      switch (activeChapterFinancial.status) {
        case CHAPTER_FINANCIAL_STATUS.ACCEPTED:
          setIsEditable(false);
          break;
        case CHAPTER_FINANCIAL_STATUS.NOT_SUBMITTED:
          setIsEditable(!isCreate);
          break;
        case CHAPTER_FINANCIAL_STATUS.SUBMITTED:
          setIsEditable(false);
          break;
        case CHAPTER_FINANCIAL_STATUS.REJECTED:
          setIsEditable(!isCreate);
          break;
        default:
          break;
      }
    }
  }, [isCreate, activeChapterFinancial]);

  useEffect(() => {
    const fetch = async () => {
      if (activeChapterFinancial) {
        const items = await getChapterFinancialByYear(
          activeChapterFinancial.year - 1,
          activeChapterFinancial.chapter.id
        );
        const prev = items?.find(
          item => item.chapter.id === activeChapterFinancial.chapter.id
        );
        if (prev) {
          const prevChapter = await getChapterFinancialById(prev.id);
          setPrevChapterFinancial(prevChapter);
        }
      }
    };
    fetch();
  }, [activeChapterFinancial]);

  const handleBack = useCallback(() => {
    history.push(`/admin/financial-report/`);
  }, []);

  const handleRefetch = useCallback(async () => {
    resetIsFetched();
    if (!isCreate) {
      getChapterFinancial(id);
    }
    getChapterFinancialsDefaults();
  }, [id]);

  const handleHelp = useCallback((type, subType) => {
    setHelpData({ type, subType });
    setHelpDialogOpen(true);
  }, []);

  const handleDownloadPDF = useCallback(() => {
    getChapterSummaryReport(id);
  }, [id]);

  const handleGoToMailLog = useCallback(() => {
    history.push(`/admin/financial-report/${id}/mail-log`);
  }, [history, id]);

  const handleRedirect = useCallback(reportID => {
    history.push(`/admin/financial-report/${reportID}`);
  }, []);

  const handleAcceptReject = useCallback(
    (type, content) => {
      (async () => {
        if (type === "accept") {
          await acceptChapterFinancial(activeChapterFinancial.id, content);
        } else {
          await rejectChapterFinancial(activeChapterFinancial.id, content);
        }

        setAcceptDialogOpen(false);
        history.push(`/admin/financial-report/`);
      })();
    },
    [activeChapterFinancial]
  );

  const getLastModified = useCallback(() => {
    const { updatedDate, createdDate } = activeChapterFinancial;
    return formatDate(updatedDate ? updatedDate : createdDate);
  }, [activeChapterFinancial]);

  const getLastModifiedBy = useCallback(() => {
    const { submittedByUserName, updatedByUserName } = activeChapterFinancial;
    return updatedByUserName ? updatedByUserName : submittedByUserName;
  }, [activeChapterFinancial]);

  const getAcceptedRejectDate = useCallback(() => {
    const { acceptedOrRejectedDate } = activeChapterFinancial;
    return formatDate(acceptedOrRejectedDate);
  }, [activeChapterFinancial]);

  const getAcceptedRejectedBy = useCallback(() => {
    const { acceptedOrRejectedBy } = activeChapterFinancial;
    return acceptedOrRejectedBy;
  }, [activeChapterFinancial]);

  const validChapterFinancial = useMemo(() => {
    return !isCreate &&
      activeChapterFinancial &&
      activeChapterFinancial.report.reportItems &&
      activeChapterFinancial.report.reportItems.length > 0
      ? activeChapterFinancial.report.reportItems
      : chapterFinancialsDefaults || [];
  }, [activeChapterFinancial, id, chapterFinancialsDefaults]);

  const getCashInvestment = useMemo(() => {
    return activeChapterFinancial
      ? activeChapterFinancial.report.financialAccounts.reduce(
          (prev, cur) => prev + cur.balance,
          0
        )
      : 0;
  }, [activeChapterFinancial]);

  const revenueItems = useMemo(() => {
    return validChapterFinancial.filter(
      item => item.reportItemTypeID === REPORT_ITEM_TYPE.REVENUE
    );
  }, [validChapterFinancial]);
  const revenueDefaultItems = useMemo(() => {
    return chapterFinancialsDefaults
      ? chapterFinancialsDefaults.filter(
          item => item.reportItemTypeID === REPORT_ITEM_TYPE.REVENUE
        )
      : [];
  }, [chapterFinancialsDefaults]);

  const expenseItems = useMemo(() => {
    return validChapterFinancial.filter(
      item => item.reportItemTypeID === REPORT_ITEM_TYPE.EXPENSE
    );
  }, [validChapterFinancial]);
  const expenseDefaultItems = useMemo(() => {
    return chapterFinancialsDefaults
      ? chapterFinancialsDefaults.filter(
          item => item.reportItemTypeID === REPORT_ITEM_TYPE.EXPENSE
        )
      : [];
  }, [chapterFinancialsDefaults]);

  const getRevenue = useMemo(() => {
    return revenueItems.reduce((prev, cur) => prev + (cur.amount || 0), 0);
  }, [revenueItems]);

  const getExpense = useMemo(() => {
    return expenseItems.reduce((prev, cur) => prev + (cur.amount || 0), 0);
  }, [expenseItems]);

  const prevCashInvestment = useMemo(() => {
    return prevChapterFinancial.report
      ? prevChapterFinancial.report.financialAccounts.reduce(
          (prev, cur) => prev + cur.balance,
          0
        )
      : 0;
  }, [prevChapterFinancial]);

  return (
    <Container>
      <Card>
        <CardHeader color="rose" text>
          <CardText color="rose">
            <Typography variant="h4">
              {" "}
              {isCreate
                ? activeChapter && activeChapter?.name
                : activeChapterFinancial?.chapter?.name}{" "}
              Report for FY{" "}
              {isCreate ? query.get("year") : activeChapterFinancial?.year}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          {(isCreate && (!chapterFinancialsDefaults || !activeChapter)) ||
          isFetching.CHAPTER_SUMMARY ||
          (!isCreate && !activeChapterFinancial) ? (
            <Grid container justify="center" alignItems="center">
              <Loading />
            </Grid>
          ) : (
            <Grid container justify="center" alignItems="center">
              <Grid item xs={12} md={6}>
                <Grid container justify="space-between">
                  <Grid item>
                    <Button
                      color="rose"
                      className={classes.marginRight}
                      simple
                      onClick={handleBack}
                    >
                      <ArrowBack className={classes.icons} /> Back to listing
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      disabled={isCreate}
                      color="rose"
                      simple
                      onClick={handleDownloadPDF}
                    >
                      Download PDF
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      disabled={
                        isCreate || activeChapterFinancial.mailLog.length === 0
                      }
                      color="rose"
                      simple
                      onClick={handleGoToMailLog}
                    >
                      Mail Log
                    </Button>
                  </Grid>
                </Grid>
                <Divider className={classes.marginBottom} />
                <Box position="relative">
                  <Typography className={classes.marginTop}>
                    Status:{" "}
                    {isCreate
                      ? "Not Submitted"
                      : CHAPTER_STATUS[activeChapterFinancial.status]}
                  </Typography>
                  {activeChapterFinancial && (
                    <>
                      <Typography className={classes.marginBottom}>
                        Submitted By:{" "}
                        {activeChapterFinancial.submittedByUserName}
                      </Typography>
                      <Typography className={classes.marginTop}>
                        Last Modified: {getLastModified()}
                      </Typography>
                      <Typography className={classes.marginBottom}>
                        Last Modified By: {getLastModifiedBy()}
                      </Typography>
                      {(activeChapterFinancial.status ===
                        CHAPTER_FINANCIAL_STATUS.REJECTED ||
                        activeChapterFinancial.status ===
                          CHAPTER_FINANCIAL_STATUS.ACCEPTED) && (
                        <>
                          <Typography className={classes.marginTop}>
                            {CHAPTER_STATUS[activeChapterFinancial.status]}{" "}
                            Date: {getAcceptedRejectDate()}
                          </Typography>
                          <Typography className={classes.marginBottom}>
                            {CHAPTER_STATUS[activeChapterFinancial.status]} By:{" "}
                            {getAcceptedRejectedBy()}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                  {activeChapterFinancial &&
                    activeChapterFinancial.status ===
                      CHAPTER_FINANCIAL_STATUS.SUBMITTED && (
                      <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        className={classes.actionWrapper}
                      >
                        <Box display="flex" mb={1}>
                          <a
                            onClick={() => {
                              setAcceptDialogOpen(true);
                              setDialogType("accept");
                            }}
                          >
                            <strong>Accept</strong>
                          </a>
                          <Divider
                            orientation="vertical"
                            flexItem
                            className={classes.dividerMargin}
                          />
                          <a
                            onClick={() => {
                              setAcceptDialogOpen(true);
                              setDialogType("reject");
                            }}
                          >
                            <strong>Reject</strong>
                          </a>
                        </Box>
                        <Button
                          color="rose"
                          onClick={() => {
                            setIsEditable(true);
                          }}
                          size="sm"
                        >
                          Edit This Report
                        </Button>
                      </Box>
                    )}
                </Box>
                <Grid
                  container
                  direction="column"
                  className={classes.marginBottom}
                >
                  <Grid item>
                    <Grid container justify="space-between" alignItems="center">
                      <Typography>
                        <strong>Notes:</strong>
                      </Typography>
                    </Grid>
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          (activeChapterFinancial &&
                            activeChapterFinancial.report.adminNotes) ||
                          ""
                      }}
                    ></div>
                  </Grid>
                </Grid>
                <NavPills
                  active={activeTab}
                  setActive={setActiveTab}
                  color="rose"
                  tabs={[
                    {
                      tabButton: "Home",
                      tabContent: (
                        <TabContent active={activeTab} setActive={setActiveTab}>
                          <WelcomeContent />
                        </TabContent>
                      )
                    },
                    {
                      tabButton: "Revenue",
                      tabContent: (
                        <TabContent active={activeTab} setActive={setActiveTab}>
                          <RevenueContent
                            reportID={id}
                            chapterFinancial={activeChapterFinancial}
                            items={revenueItems}
                            defaultItems={revenueDefaultItems}
                            totalRevenue={getRevenue}
                            onRefetch={handleRefetch}
                            onHelp={handleHelp}
                            onRedirect={handleRedirect}
                            createReportItem={createReportItem}
                            updateReportItem={updateReportItem}
                            removeReportItem={removeReportItem}
                            updateReportItems={updateReportItems}
                            updateReportItemsPut={updateReportItemsPut}
                            initialReportItems={initialReportItems}
                            isEditable={isEditable}
                          />
                        </TabContent>
                      )
                    },
                    {
                      tabButton: "Expenses",
                      tabContent: (
                        <TabContent active={activeTab} setActive={setActiveTab}>
                          <ExpenseContent
                            reportID={id}
                            chapterFinancial={activeChapterFinancial}
                            items={expenseItems}
                            defaultItems={expenseDefaultItems}
                            totalExpense={getExpense}
                            onRefetch={handleRefetch}
                            onHelp={handleHelp}
                            onRedirect={handleRedirect}
                            createReportItem={createReportItem}
                            updateReportItem={updateReportItem}
                            updateReportItemsPut={updateReportItemsPut}
                            removeReportItem={removeReportItem}
                            updateReportItems={updateReportItems}
                            initialReportItems={initialReportItems}
                            updateReportW2={updateReportW2}
                            updateVotingCount={updateVotingCount}
                            isEditable={isEditable}
                          />
                        </TabContent>
                      )
                    },
                    {
                      tabButton: "Cash & Investments",
                      tabContent: (
                        <TabContent active={activeTab} setActive={setActiveTab}>
                          <CashContent
                            reportID={id}
                            chapterFinancial={activeChapterFinancial}
                            totalCash={getCashInvestment}
                            createFinancialAccount={createFinancialAccount}
                            updateFinancialAccount={updateFinancialAccount}
                            removeFinancialAccount={removeFinancialAccount}
                            onRefetch={handleRefetch}
                            onHelp={handleHelp}
                            onRedirect={handleRedirect}
                            isEditable={isEditable}
                          />
                        </TabContent>
                      )
                    },
                    {
                      tabButton: "Summary",
                      tabContent: (
                        <TabContent
                          active={activeTab}
                          setActive={setActiveTab}
                          isEnd={true}
                        >
                          <SummaryContent
                            reportID={id}
                            totalRevenue={getRevenue}
                            totalExpense={getExpense}
                            totalCash={getCashInvestment}
                            prevCash={prevCashInvestment}
                            months={getCashInvestment / (getExpense / 12)}
                            year={
                              activeChapterFinancial
                                ? activeChapterFinancial.year
                                : 0
                            }
                            submitChapterFinancial={submitChapterFinancial}
                            isEditable={
                              activeChapterFinancial &&
                              activeChapterFinancial.status !==
                                CHAPTER_FINANCIAL_STATUS.ACCEPTED &&
                              isEditable
                            }
                            onRefetch={handleRefetch}
                            onHelp={handleHelp}
                            notes={activeChapterFinancial?.report?.notes}
                          />
                        </TabContent>
                      )
                    }
                  ]}
                />
              </Grid>
            </Grid>
          )}
        </CardBody>
        {activeChapterFinancial && (
          <AcceptReportDialog
            open={acceptDialogOpen}
            type={dialogType}
            name={activeChapterFinancial.chapter.name}
            year={activeChapterFinancial.year}
            content={activeChapterFinancial.report.adminNotes}
            onSave={handleAcceptReject}
            onClose={() => setAcceptDialogOpen(false)}
          />
        )}
        <HelpDialog
          open={isHelpDialogOpen}
          data={helpData}
          onClose={() => setHelpDialogOpen(false)}
        />
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  isFetching: state.adminReducer.isFetching,
  isFetched: state.adminReducer.isFetched,
  activeChapterFinancial: state.adminReducer.activeChapterFinancial,
  activeChapter: state.adminReducer.activeChapter,
  chapterFinancialsDefaults: state.adminReducer.chapterFinancialsDefaults
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FinancialReportForm);
